import { ReactElement, useEffect, useState } from "react";
import { Table } from "./Table";
import { useAtom } from "jotai";
import { asnsTableAtom } from "../other/store";
import {
  getColumns,
  getCustomOptions,
  getRowID,
  onApplyFiltersButtonClick,
  onViewColumnsChange,
} from "../utils/components/autonomousSystemsTable";
import {
  countAtom,
  expandedRowIDsAtom,
  isDataSourcesNumberVisibleAtom,
  isDataSourcesPercentageVisibleAtom,
  isNumberVisibleAtom,
  maxDataSourcesNumberFilterAtom,
  maxDataSourcesPercentageFilterAtom,
  minDataSourcesNumberFilterAtom,
  minDataSourcesPercentageFilterAtom,
  numberFilterAtom,
  pageAtom,
  rowsPerPageAtom,
} from "../store/autonomousSystemsTable";
import { usePagination } from "../hooks/usePagination";
import { ShareUrlButton } from "./ShareUrlButton";
import { Snackbar } from "@mui/material";
import { Stack } from "@mui/system";
import { Alert } from "./Alert";
import { PATHS } from "../constants/paths";

/**
 * @param {object} props
 * @param {object []} props.urlParams
 * @returns {ReactElement}
 */
export const AutonomousSystemsTable = ({ urlParams }) => {
  const [
    {
      data: autonomousSystems,
      fetching: autonomousSystemsFetching,
      error: autonomousSystemsError,
    },
  ] = useAtom(asnsTableAtom);
  const [numberFilter, setNumberFilter] = useAtom(numberFilterAtom);
  const [minDataSourcesNumberFilter, setMinDataSourcesNumberFilter] = useAtom(
    minDataSourcesNumberFilterAtom
  );
  const [maxDataSourcesNumberFilter, setMaxDataSourcesNumberFilter] = useAtom(
    maxDataSourcesNumberFilterAtom
  );
  const [minDataSourcesPercentageFilter, setMinDataSourcesPercentageFilter] =
    useAtom(minDataSourcesPercentageFilterAtom);
  const [maxDataSourcesPercentageFilter, setMaxDataSourcesPercentageFilter] =
    useAtom(maxDataSourcesPercentageFilterAtom);
  const [isNumberVisible, setIsNumberVisible] = useAtom(isNumberVisibleAtom);
  const [isDataSourcesNumberVisible, setIsDataSourcesNumberVisible] = useAtom(
    isDataSourcesNumberVisibleAtom
  );
  const [isDataSourcesPercentageVisible, setIsDataSourcesPercentageVisible] =
    useAtom(isDataSourcesPercentageVisibleAtom);
  const { resetPage } = usePagination({ pageAtom, rowsPerPageAtom });
  const [count, setCount] = useAtom(countAtom);
  const [expandedRowIDs, setExpandedRowIDs] = useAtom(expandedRowIDsAtom);
  const [copiedURL, setCopiedURL] = useState(false);

  useEffect(() => {
    if (urlParams.length !== 0) {
      urlParams.forEach((item) => {
        switch (item[0]) {
          case "numberFilter":
            setNumberFilter(item[1]);
            break;
          case "minDataSourcesNumberFilter":
            setMinDataSourcesNumberFilter(item[1]);
            break;
          case "maxDataSourcesNumberFilter":
            setMaxDataSourcesNumberFilter(item[1]);
            break;
          case "minDataSourcesPercentageFilter":
            setMinDataSourcesPercentageFilter(item[1]);
            break;
          case "maxDataSourcesPercentageFilter":
            setMaxDataSourcesPercentageFilter(item[1]);
            break;
          default:
            console.error("Invalid URL parameter!");
        }
      });
    }
  }, []);

  /**
   * @param {object} params
   * @param {Function} params.applyNewFilters
   * @returns {void}
   */
  const onApplyFiltersButtonClickCallback = ({ applyNewFilters }) => {
    onApplyFiltersButtonClick({
      applyNewFilters,
      setNumberFilter,
      setMinDataSourcesNumberFilter,
      setMaxDataSourcesNumberFilter,
      setMinDataSourcesPercentageFilter,
      setMaxDataSourcesPercentageFilter,
      resetPage,
    });
  };

  /**
   * @param {object} params
   * @param {string} params.changedColumn
   * @param {string} params.action
   * @returns {void}
   */
  const onViewColumnsChangeCallback = ({ changedColumn, action }) => {
    onViewColumnsChange({
      changedColumn,
      action,
      setIsNumberVisible,
      setIsDataSourcesNumberVisible,
      setIsDataSourcesPercentageVisible,
    });
  };

  const columns = getColumns({
    rows: autonomousSystems,
    numberFilter,
    minDataSourcesNumberFilter,
    maxDataSourcesNumberFilter,
    minDataSourcesPercentageFilter,
    maxDataSourcesPercentageFilter,
    setMinDataSourcesNumberFilter,
    setMaxDataSourcesNumberFilter,
    setMinDataSourcesPercentageFilter,
    setMaxDataSourcesPercentageFilter,
    isNumberVisible,
    isDataSourcesNumberVisible,
    isDataSourcesPercentageVisible,
  });
  const customOptions = getCustomOptions({
    autonomousSystems,
  });

  const shareUrlData = () => {
    let filters = "";
    if (numberFilter !== "" && typeof numberFilter !== "undefined") {
      if (filters === "") filters += "?";
      filters += "numberFilter=" + numberFilter + "&";
    }
    if (
      minDataSourcesNumberFilter !== "" &&
      typeof minDataSourcesNumberFilter !== "undefined"
    ) {
      if (filters === "") filters += "?";
      filters +=
        "minDataSourcesNumberFilter=" + minDataSourcesNumberFilter + "&";
    }
    if (
      maxDataSourcesNumberFilter !== "" &&
      typeof maxDataSourcesNumberFilter !== "undefined"
    ) {
      if (filters === "") filters += "?";
      filters +=
        "maxDataSourcesNumberFilter=" + maxDataSourcesNumberFilter + "&";
    }
    if (
      minDataSourcesPercentageFilter !== "" &&
      typeof minDataSourcesPercentageFilter !== "undefined"
    ) {
      if (filters === "") filters += "?";
      filters +=
        "minDataSourcesPercentageFilter=" +
        minDataSourcesPercentageFilter +
        "&";
    }
    if (
      maxDataSourcesPercentageFilter !== "" &&
      typeof maxDataSourcesPercentageFilter !== "undefined"
    ) {
      if (filters === "") filters += "?";
      filters +=
        "maxDataSourcesPercentageFilter=" + maxDataSourcesPercentageFilter;
    }

    const serviceURL = `https://${window.location.host}${PATHS.dashboard.lookingglass.index}${filters}#autonomous-systems`;
    navigator.clipboard.writeText(serviceURL);
    setCopiedURL(true);
  };

  const handleClose = () => {
    setCopiedURL(false);
  };

  const HeaderElements = () => (
    <>
      <ShareUrlButton onClick={shareUrlData} toolbarIconOrder={-1} />
    </>
  );

  return (
    <Stack>
      <Table
        data={autonomousSystems}
        loading={autonomousSystemsFetching}
        error={autonomousSystemsError}
        columns={columns}
        customOptions={{
          responsive: "standard",
          tableBodyMaxHeight: "70vh",
          customToolbar: HeaderElements,
          textLabels: {
            body: {
              noMatch: "No Autonomous Systems found.",
            },
          },
          ...customOptions,
        }}
        pageAtom={pageAtom}
        rowsPerPageAtom={rowsPerPageAtom}
        count={count}
        setCount={setCount}
        onApplyFiltersButtonClick={onApplyFiltersButtonClickCallback}
        onViewColumnsChange={onViewColumnsChangeCallback}
        getRowID={getRowID}
        expandedRowIDs={expandedRowIDs}
        setExpandedRowIDs={setExpandedRowIDs}
        exportDataFileName={"Autonomous Systems Table Looking Glass Data"}
      />
      <Snackbar
        open={copiedURL}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <Alert onClose={handleClose} severity="success">
          Share link copied to clipboard
        </Alert>
      </Snackbar>
    </Stack>
  );
};
